import type {
  TombstoneProduct,
  TombstoneQuote,
  TombstoneQuoteCustomItem,
  TombstoneQuoteExtraItem,
  CreateTombstoneProductRequest,
} from '../types/tombstones';
import { apiRequest } from './api-client';

const TOMBSTONES = '/backoffice/tombstones';

// ===== Products =====

export async function fetchProducts(token: string, params?: { search?: string; includeInactive?: boolean }): Promise<TombstoneProduct[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.includeInactive) query.set('includeInactive', 'true');
  return apiRequest(`${TOMBSTONES}/products?${query}`, token);
}

export async function fetchProduct(id: string, token: string): Promise<TombstoneProduct> {
  return apiRequest(`${TOMBSTONES}/products/${id}`, token);
}

export async function createProduct(data: CreateTombstoneProductRequest, token: string): Promise<TombstoneProduct> {
  return apiRequest(`${TOMBSTONES}/products`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProduct(id: string, data: Partial<CreateTombstoneProductRequest>, token: string): Promise<TombstoneProduct> {
  return apiRequest(`${TOMBSTONES}/products/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteProduct(id: string, token: string): Promise<void> {
  await apiRequest(`${TOMBSTONES}/products/${id}`, token, { method: 'DELETE' });
}

export async function toggleProductStatus(id: string, token: string): Promise<TombstoneProduct> {
  return apiRequest(`${TOMBSTONES}/products/${id}/status`, token, { method: 'PATCH' });
}

// ===== Quotes =====

export async function fetchQuotes(token: string, params?: { search?: string; status?: string; startDate?: string; endDate?: string }): Promise<TombstoneQuote[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.status) query.set('status', params.status);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return apiRequest(`${TOMBSTONES}/quotes?${query}`, token);
}

export async function fetchQuote(id: string, token: string): Promise<TombstoneQuote> {
  return apiRequest(`${TOMBSTONES}/quotes/${id}`, token);
}

export interface CreateTombstoneQuoteRequest {
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerCompany?: string;
  customerDocument?: string;
  customerSegment?: string;
  notes?: string;
}

export async function createQuote(data: CreateTombstoneQuoteRequest, token: string): Promise<TombstoneQuote> {
  return apiRequest(`${TOMBSTONES}/quotes`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQuoteStatus(id: string, status: string, token: string): Promise<TombstoneQuote> {
  return apiRequest(`${TOMBSTONES}/quotes/${id}/status`, token, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function markTombstoneQuoteAsViewed(quoteId: string, token: string): Promise<TombstoneQuote> {
  return apiRequest(`${TOMBSTONES}/quotes/${quoteId}/viewed`, token, {
    method: 'POST',
  });
}

// ===== Quote Item Management =====

export async function addQuoteItem(quoteId: string, data: { productId: string; quantity: number; dimensionId?: string }, token: string): Promise<any> {
  return apiRequest(`${TOMBSTONES}/quotes/${quoteId}/items`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQuoteItem(itemId: string, data: { quantity: number }, token: string): Promise<any> {
  return apiRequest(`${TOMBSTONES}/quotes/items/${itemId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteQuoteItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${TOMBSTONES}/quotes/items/${itemId}`, token, { method: 'DELETE' });
}

// ===== Custom Items =====

export async function fetchPendingCustomItems(token: string): Promise<TombstoneQuoteCustomItem[]> {
  return apiRequest(`${TOMBSTONES}/quotes/pending-custom-items`, token);
}

export interface ConfirmCustomItemPricePayload {
  confirmedPrice?: number;
}

export async function confirmCustomItemPrice(itemId: string, payload: ConfirmCustomItemPricePayload, token: string): Promise<TombstoneQuoteCustomItem> {
  return apiRequest(`${TOMBSTONES}/quotes/custom-items/${itemId}/confirm-price`, token, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function deleteCustomItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${TOMBSTONES}/quotes/custom-items/${itemId}`, token, { method: 'DELETE' });
}

// ===== Quote Extra Items =====

export async function addExtraItem(quoteId: string, description: string, value: number, token: string): Promise<TombstoneQuoteExtraItem> {
  return apiRequest(`${TOMBSTONES}/quotes/${quoteId}/extra-items`, token, {
    method: 'POST',
    body: JSON.stringify({ description, value }),
  });
}

export async function deleteExtraItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${TOMBSTONES}/quotes/extra-items/${itemId}`, token, { method: 'DELETE' });
}

export async function updateExtraItems(
  quoteId: string,
  items: Array<{ description: string; value: number }>,
  token: string
): Promise<void> {
  await apiRequest(`${TOMBSTONES}/quotes/${quoteId}/extra-items`, token, {
    method: 'PUT',
    body: JSON.stringify(items),
  });
}

// ===== Quote Discount =====

export async function updateQuoteDiscount(quoteId: string, discount: string, token: string): Promise<TombstoneQuote> {
  return apiRequest(`${TOMBSTONES}/quotes/${quoteId}/discount`, token, {
    method: 'PATCH',
    body: JSON.stringify({ discount }),
  });
}
